// AdminUserManagement.tsx
import * as React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  deleteEmployeeAsAdmin,
  fetchAllEmployeesForAdmin,
  fetchCompanies,
  fetchManagerViewerByAuthUserId,
  getCurrentSession,
  getCurrentUser,
  updateEmployeeAdminFields,
  updateEmployeeCompanyRole,
} from "../api/queries";
import { useI18n } from "../i18n/I18nProvider";
import type { AdminEmployeeRow } from "../types/adminUsers";
import type { CompanyRole } from "../types/navigation";
import type { CompanyRow } from "../types/company";

function SectionIconBox(props: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        width: 44,
        height: 44,
        borderRadius: 2.5,
        bgcolor: "#FFFFFF",
        border: "1px solid #E5E7EB",
        display: "grid",
        placeItems: "center",
        color: "#111827",
        flexShrink: 0,
      }}
    >
      {props.children}
    </Box>
  );
}

const thSx = {
  color: "#6B7280",
  fontWeight: 900,
  bgcolor: "#F9FAFB",
  borderBottom: "1px solid #E5E7EB",
} as const;

const ROLE_OPTIONS: { value: CompanyRole; labelEn: string; labelDe: string }[] = [
  { value: "employee", labelEn: "Employee", labelDe: "Mitarbeitende" },
  { value: "manager", labelEn: "Manager", labelDe: "Manager" },
  { value: "admin", labelEn: "Admin", labelDe: "Admin" },
];

const ROLE_CHIP_STYLES: Record<string, { bgcolor: string; color: string; borderColor: string }> = {
  admin: { bgcolor: "#F5F3FF", color: "#6D28D9", borderColor: "#DDD6FE" },
  manager: { bgcolor: "#EFF6FF", color: "#1D4ED8", borderColor: "#BFDBFE" },
  employee: { bgcolor: "#F0FDF4", color: "#15803D", borderColor: "#BBF7D0" },
};

function employeeFullName(row: AdminEmployeeRow) {
  return `${row.first_name ?? ""} ${row.last_name ?? ""}`.trim() || "—";
}

export default function AdminUserManagement(props: { handlePageChange: (page: string) => void }) {
  void props;

  const { lang } = useI18n();
  const typedLang: "en" | "de" = lang === "de" ? "de" : "en";
  const L = React.useCallback((en: string, de: string) => (typedLang === "de" ? de : en), [typedLang]);

  const [loading, setLoading] = React.useState(true);
  const [uiError, setUiError] = React.useState<string | null>(null);
  const [rows, setRows] = React.useState<AdminEmployeeRow[]>([]);
  const [companies, setCompanies] = React.useState<CompanyRow[]>([]);
  const [myEmployeeId, setMyEmployeeId] = React.useState<string | null>(null);
  const [search, setSearch] = React.useState("");
  const [savingId, setSavingId] = React.useState<string | null>(null);
  const [rowError, setRowError] = React.useState<Record<string, string>>({});

  const [editTarget, setEditTarget] = React.useState<AdminEmployeeRow | null>(null);
  const [editCompanyId, setEditCompanyId] = React.useState("");
  const [editDepartment, setEditDepartment] = React.useState("");
  const [editSaving, setEditSaving] = React.useState(false);
  const [editError, setEditError] = React.useState<string | null>(null);

  const [deleteTarget, setDeleteTarget] = React.useState<AdminEmployeeRow | null>(null);
  const [deleting, setDeleting] = React.useState(false);
  const [deleteError, setDeleteError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let mounted = true;

    async function load() {
      setLoading(true);
      setUiError(null);

      try {
        const { data: userData, error: userErr } = await getCurrentUser();
        if (userErr) throw userErr;
        if (!userData?.user) {
          throw new Error(L("Not authenticated.", "Nicht angemeldet."));
        }

        const { data: me, error: meErr } = await fetchManagerViewerByAuthUserId(userData.user.id);
        if (meErr) throw meErr;
        if (!me?.id || String(me.company_role) !== "admin") {
          throw new Error(L("You do not have admin access.", "Du hast keinen Admin-Zugriff."));
        }

        const [{ data, error }, { data: companyRows, error: companiesErr }] = await Promise.all([
          fetchAllEmployeesForAdmin(),
          fetchCompanies(),
        ]);

        if (error) throw error;
        if (companiesErr) throw companiesErr;
        if (!mounted) return;

        setMyEmployeeId(me.id);
        setRows((data ?? []) as unknown as AdminEmployeeRow[]);
        setCompanies((companyRows ?? []) as CompanyRow[]);
      } catch (err) {
        if (!mounted) return;
        setUiError(
          err instanceof Error ? err.message : L("Something went wrong.", "Etwas ist schiefgelaufen.")
        );
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();
    return () => {
      mounted = false;
    };
  }, [L]);

  const filteredRows = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;

    return rows.filter((row) => {
      const name = employeeFullName(row).toLowerCase();
      const email = (row.company_email ?? "").toLowerCase();
      const company = (row.companies?.name ?? "").toLowerCase();
      return name.includes(q) || email.includes(q) || company.includes(q);
    });
  }, [rows, search]);

  async function handleRoleChange(employeeId: string, nextRole: CompanyRole) {
    const previousRows = rows;
    setSavingId(employeeId);
    setRowError((prev) => ({ ...prev, [employeeId]: "" }));
    setRows((prev) => prev.map((row) => (row.id === employeeId ? { ...row, company_role: nextRole } : row)));

    const { error } = await updateEmployeeCompanyRole(employeeId, nextRole);

    if (error) {
      setRows(previousRows);
      setRowError((prev) => ({
        ...prev,
        [employeeId]: L("Failed to update role.", "Rolle konnte nicht aktualisiert werden."),
      }));
    }

    setSavingId(null);
  }

  function openEditDialog(row: AdminEmployeeRow) {
    setEditTarget(row);
    setEditCompanyId(row.company_id ?? "");
    setEditDepartment(row.department ?? "");
    setEditError(null);
  }

  function closeEditDialog() {
    if (editSaving) return;
    setEditTarget(null);
  }

  async function handleSaveEdit() {
    if (!editTarget) return;

    setEditSaving(true);
    setEditError(null);

    const { error } = await updateEmployeeAdminFields(editTarget.id, {
      company_id: editCompanyId,
      department: editDepartment.trim(),
    });

    if (error) {
      setEditError(L("Failed to save changes.", "Änderungen konnten nicht gespeichert werden."));
      setEditSaving(false);
      return;
    }

    const company = companies.find((c) => c.id === editCompanyId) ?? null;
    setRows((prev) =>
      prev.map((row) =>
        row.id === editTarget.id
          ? {
              ...row,
              company_id: editCompanyId,
              department: editDepartment.trim(),
              companies: company ? { name: company.name } : row.companies,
            }
          : row
      )
    );

    setEditSaving(false);
    setEditTarget(null);
  }

  function openDeleteDialog(row: AdminEmployeeRow) {
    setDeleteTarget(row);
    setDeleteError(null);
  }

  function closeDeleteDialog() {
    if (deleting) return;
    setDeleteTarget(null);
  }

  async function handleConfirmDelete() {
    if (!deleteTarget) return;

    setDeleting(true);
    setDeleteError(null);

    try {
      const { data: sess, error: sessErr } = await getCurrentSession();
      const token = sess.session?.access_token;
      if (sessErr || !token) {
        throw new Error(L("Not authenticated.", "Nicht angemeldet."));
      }

      const { data, error } = await deleteEmployeeAsAdmin(deleteTarget.id, token);
      const failure = error || (data && "error" in data ? data.error : null);
      if (failure) throw new Error(typeof failure === "string" ? failure : String(failure));

      setRows((prev) => prev.filter((row) => row.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (err) {
      setDeleteError(
        err instanceof Error ? err.message : L("Failed to delete user.", "Nutzer konnte nicht gelöscht werden.")
      );
    } finally {
      setDeleting(false);
    }
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#F3F4F6", p: { xs: 2, md: 3 } }}>
      <Box sx={{ width: "100%", maxWidth: 1180, mx: "auto" }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <SectionIconBox>
              <ManageAccountsOutlinedIcon />
            </SectionIconBox>

            <Box>
              <Typography variant="h5" sx={{ fontWeight: 900, color: "#111827", lineHeight: 1.1 }}>
                {L("User Rights", "Nutzerrechte")}
              </Typography>
              <Typography variant="body2" sx={{ color: "#6B7280" }}>
                {L(
                  "Set roles, company and department, or remove a registered user.",
                  "Lege Rolle, Unternehmen und Abteilung fest oder entferne eine registrierte Person."
                )}
              </Typography>
            </Box>
          </Stack>

          <Chip
            label={`${L("Users", "Nutzer")}: ${loading ? "…" : rows.length}`}
            variant="outlined"
            sx={{
              height: 42,
              borderRadius: 2.5,
              borderColor: "#D1D5DB",
              bgcolor: "#FFFFFF",
              color: "#111827",
              fontWeight: 900,
              px: 1,
              minWidth: 140,
              justifyContent: "center",
            }}
          />
        </Stack>

        {uiError ? (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2.5 }}>
            {uiError}
          </Alert>
        ) : null}

        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border: "1px solid #E5E7EB",
            bgcolor: "#FFFFFF",
            overflow: "hidden",
          }}
        >
          <Box sx={{ p: { xs: 2, md: 2.5 } }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.5}
              alignItems={{ xs: "stretch", sm: "center" }}
              justifyContent="space-between"
              sx={{ mb: 2 }}
            >
              <Typography sx={{ fontWeight: 900, color: "#111827" }}>
                {L("Registered users", "Registrierte Nutzer")}
              </Typography>

              <TextField
                size="small"
                placeholder={L("Search by name, email or company", "Suche nach Name, E-Mail oder Unternehmen")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ minWidth: { xs: "100%", sm: 280 } }}
              />
            </Stack>

            {loading ? (
              <Stack spacing={1}>
                <Skeleton variant="rounded" height={56} />
                <Skeleton variant="rounded" height={56} />
                <Skeleton variant="rounded" height={56} />
              </Stack>
            ) : filteredRows.length === 0 ? (
              <Typography variant="body2" sx={{ color: "#6B7280", py: 2 }}>
                {L("No users found.", "Keine Nutzer gefunden.")}
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={thSx}>{L("Name", "Name")}</TableCell>
                      <TableCell sx={thSx}>{L("Email", "E-Mail")}</TableCell>
                      <TableCell sx={thSx}>{L("Company", "Unternehmen")}</TableCell>
                      <TableCell sx={thSx}>{L("Department", "Abteilung")}</TableCell>
                      <TableCell sx={thSx} align="right">
                        {L("Role", "Rolle")}
                      </TableCell>
                      <TableCell sx={thSx} align="right">
                        {L("Actions", "Aktionen")}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredRows.map((row) => {
                      const chipStyle = ROLE_CHIP_STYLES[String(row.company_role)] ?? ROLE_CHIP_STYLES.employee;
                      const isSelf = row.id === myEmployeeId;

                      return (
                        <TableRow key={row.id} hover>
                          <TableCell sx={{ fontWeight: 700, color: "#111827" }}>{employeeFullName(row)}</TableCell>
                          <TableCell sx={{ color: "#374151" }}>{row.company_email ?? "—"}</TableCell>
                          <TableCell sx={{ color: "#374151" }}>{row.companies?.name ?? "—"}</TableCell>
                          <TableCell sx={{ color: "#374151" }}>{row.department ?? "—"}</TableCell>
                          <TableCell align="right">
                            <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                              {savingId === row.id ? <CircularProgress size={16} /> : null}
                              <Select
                                size="small"
                                value={row.company_role}
                                onChange={(e) => handleRoleChange(row.id, e.target.value as CompanyRole)}
                                disabled={savingId === row.id}
                                sx={{
                                  minWidth: 140,
                                  borderRadius: 999,
                                  fontWeight: 900,
                                  bgcolor: chipStyle.bgcolor,
                                  color: chipStyle.color,
                                  "& .MuiOutlinedInput-notchedOutline": { borderColor: chipStyle.borderColor },
                                }}
                              >
                                {ROLE_OPTIONS.map((opt) => (
                                  <MenuItem key={opt.value} value={opt.value}>
                                    {L(opt.labelEn, opt.labelDe)}
                                  </MenuItem>
                                ))}
                              </Select>
                            </Stack>
                            {rowError[row.id] ? (
                              <Typography variant="caption" sx={{ color: "#B91C1C", display: "block", mt: 0.5 }}>
                                {rowError[row.id]}
                              </Typography>
                            ) : null}
                          </TableCell>
                          <TableCell align="right">
                            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                              <Tooltip title={L("Edit company / department", "Unternehmen / Abteilung bearbeiten")}>
                                <IconButton size="small" onClick={() => openEditDialog(row)}>
                                  <EditOutlinedIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip
                                title={
                                  isSelf
                                    ? L("You can't delete your own account here", "Du kannst dein eigenes Konto hier nicht löschen")
                                    : L("Delete user", "Nutzer löschen")
                                }
                              >
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={isSelf}
                                    onClick={() => openDeleteDialog(row)}
                                    sx={{ color: isSelf ? undefined : "#B91C1C" }}
                                  >
                                    <DeleteOutlineIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </Paper>
      </Box>

      <Dialog open={!!editTarget} onClose={closeEditDialog} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 900 }}>
          {L("Edit user", "Nutzer bearbeiten")}
          {editTarget ? (
            <Typography variant="body2" sx={{ color: "#6B7280", fontWeight: 400 }}>
              {employeeFullName(editTarget)}
            </Typography>
          ) : null}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 0.5 }}>
            {editError ? (
              <Alert severity="error" sx={{ borderRadius: 2.5 }}>
                {editError}
              </Alert>
            ) : null}

            <TextField
              select
              fullWidth
              label={L("Company", "Unternehmen")}
              value={editCompanyId}
              onChange={(e) => setEditCompanyId(e.target.value)}
            >
              {companies.map((company) => (
                <MenuItem key={company.id} value={company.id}>
                  {company.name}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              fullWidth
              label={L("Department", "Abteilung")}
              value={editDepartment}
              onChange={(e) => setEditDepartment(e.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={closeEditDialog} disabled={editSaving}>
            {L("Cancel", "Abbrechen")}
          </Button>
          <Button variant="contained" onClick={handleSaveEdit} disabled={editSaving || !editCompanyId}>
            {editSaving ? <CircularProgress size={18} /> : L("Save", "Speichern")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteTarget} onClose={closeDeleteDialog} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 900, color: "#B91C1C" }}>
          {L("Delete user?", "Nutzer löschen?")}
        </DialogTitle>
        <DialogContent>
          {deleteError ? (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2.5 }}>
              {deleteError}
            </Alert>
          ) : null}
          <Typography variant="body2" sx={{ color: "#374151" }}>
            {L(
              `Are you sure you want to permanently delete ${deleteTarget ? employeeFullName(deleteTarget) : ""}? This removes their account and all associated data, and cannot be undone.`,
              `Möchtest du ${deleteTarget ? employeeFullName(deleteTarget) : ""} wirklich endgültig löschen? Dadurch werden das Konto und alle zugehörigen Daten entfernt. Dies kann nicht rückgängig gemacht werden.`
            )}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={closeDeleteDialog} disabled={deleting}>
            {L("Cancel", "Abbrechen")}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            disabled={deleting}
          >
            {deleting ? <CircularProgress size={18} sx={{ color: "#FFFFFF" }} /> : L("Delete", "Löschen")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
